import React, { useRef, useState } from 'react';
import { apiFetch } from '../api';
import JSONModal from './JSONModal';
import ImageViewer from './ImageViewer';

const UPLOAD_COLUMNS = ['annotation', 'label', 'field_keypoints'] as const;

interface DatasetTableProps {
    data: any[];
    onRefresh?: () => void;
}

const DatasetTable: React.FC<DatasetTableProps> = ({ data, onRefresh }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState<any>(null);
    const [modalTitle, setModalTitle] = useState('');

    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [currentImagePrefix, setCurrentImagePrefix] = useState('');
    const [currentAnnotationPath, setCurrentAnnotationPath] = useState('');
    const [currentLabelPath, setCurrentLabelPath] = useState('');
    const [currentCameraType, setCurrentCameraType] = useState('');
    const [currentDataType, setCurrentDataType] = useState('');
    const [uploadRowIndex, setUploadRowIndex] = useState<number | null>(null);
    const [uploadColumn, setUploadColumn] = useState<string>(UPLOAD_COLUMNS[0]);
    const [uploading, setUploading] = useState(false);
    const [sportFilter, setSportFilter] = useState('');
    const [cameraFilter, setCameraFilter] = useState('');
    const [dataTypeFilter, setDataTypeFilter] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!data || data.length === 0) {
        return <div>No datasets found.</div>;
    }

    const rawColumns = Array.from(new Set(data.flatMap(Object.keys)));
    
    // Custom column ordering
    const columnOrder: Record<string, number> = {};
    rawColumns.forEach((col, idx) => { columnOrder[col] = idx; });
    // Place dup_schedule_id right after schedule_id
    if (columnOrder['schedule_id'] !== undefined && columnOrder['dup_schedule_id'] !== undefined) {
        columnOrder['dup_schedule_id'] = columnOrder['schedule_id'] + 0.1;
    }
    // Place homography right after raw_images
    if (columnOrder['raw_images'] !== undefined) {
        const rawImgIdx = columnOrder['raw_images'];
        if (columnOrder['homography'] !== undefined) columnOrder['homography'] = rawImgIdx + 0.1;
    }
    // Place video_parameter_json right after parameter_json
    if (columnOrder['parameter_json'] !== undefined && columnOrder['video_parameter_json'] !== undefined) {
        columnOrder['video_parameter_json'] = columnOrder['parameter_json'] + 0.1;
    }
    // Place curve at the end
    const maxIdx = rawColumns.length;
    if (columnOrder['curve'] !== undefined) columnOrder['curve'] = maxIdx + 1;
    
    const columns = [...rawColumns].sort((a, b) => columnOrder[a] - columnOrder[b]);

    const sportTypes = Array.from(new Set(data.map(row => row.sport_type).filter(Boolean))).sort();
    const cameraTypes = Array.from(new Set(data.map(row => row.camera_type).filter(Boolean))).sort();
    const dataTypes = Array.from(new Set(data.map(row => row.data_type).filter(Boolean))).sort();
    const filteredData = data.filter(row =>
        (!sportFilter || row.sport_type === sportFilter) &&
        (!cameraFilter || row.camera_type === cameraFilter) &&
        (!dataTypeFilter || row.data_type === dataTypeFilter)
    );

    const dataTypeCounts = filteredData.reduce((acc: Record<string, number>, row) => {
        const type = row.data_type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});

    const totalFrames = filteredData.reduce((sum, row) => sum + (row.frame_count || 0), 0);

    const handleViewJson = (key: string, value: any) => {
        setModalData(value);
        setModalTitle(key);
        setModalOpen(true);
    };

    const handleViewImages = (prefix: string, annotationPath: string, labelPath: string, cameraType: string, dataType: string) => {
        setCurrentImagePrefix(prefix);
        setCurrentAnnotationPath(annotationPath);
        setCurrentLabelPath(labelPath);
        setCurrentCameraType(cameraType);
        setCurrentDataType(dataType);
        setImageViewerOpen(true);
    };

    const isJsonColumn = (key: string, value: any) => {
        if (key.endsWith('_json')) return true;
        if (typeof value === 'object' && value !== null) return true;
        // Check if string looks like JSON (basic check)
        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
             try {
                 JSON.parse(value);
                 return true;
             } catch (e) {
                 return false;
             }
        }
        return false;
    };

    const convertGcsToHttp = (url: string) => {
        if (url.startsWith('gs://')) {
            const isDirectory = url.endsWith('/');
            const httpBase = 'https://console.cloud.google.com/storage/browser';
            const path = url.replace('gs://', '');
            
            if (isDirectory) {
                return `${httpBase}/${path}`;
            } else {
                return `${httpBase}/_details/${path}`;
            }
        }
        return url;
    };

    const isGcsColumn = (key: string) => {
        return ['raw_images', 'annotation', 'label', 'curve', 'homography'].includes(key);
    };


    const handleUpload = async (row: any) => {
        const file = fileInputRef.current?.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('schedule_id', String(row.schedule_id));
            formData.append('period_order', String(row.period_order));
            formData.append('chunk_idx', String(row.chunk_idx));
            formData.append('data_type', row.data_type);
            formData.append('sport_type', row.sport_type);
            formData.append('column', uploadColumn);
            formData.append('file', file);

            const res = await apiFetch('/train/upload_dataset', { method: 'POST', body: formData });
            const result = await res.json();
            if (!res.ok) {
                alert(`Upload failed: ${result.error}`);
            } else {
                alert(`Uploaded to ${result.gcs_path}`);
                setUploadRowIndex(null);
                onRefresh?.();
            }
        } catch (err: any) {
            alert(`Upload error: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <span><strong>Rows:</strong> {filteredData.length} / {data.length}</span>
                <span><strong>Total Frames:</strong> {totalFrames}</span>
                {Object.entries(dataTypeCounts).map(([type, count]) => (
                    <span key={type}><strong>{type}:</strong> {count}</span>
                ))}
                <label>
                    sport_type{' '}
                    <select value={sportFilter} onChange={e => setSportFilter(e.target.value)}>
                        <option value="">All</option>
                        {sportTypes.map(value => <option key={value} value={value}>{value}</option>)}
                    </select>
                </label>
                <label>
                    camera_type{' '}
                    <select value={cameraFilter} onChange={e => setCameraFilter(e.target.value)}>
                        <option value="">All</option>
                        {cameraTypes.map(value => <option key={value} value={value}>{value}</option>)}
                    </select>
                </label>
                <label>
                    data_type{' '}
                    <select value={dataTypeFilter} onChange={e => setDataTypeFilter(e.target.value)}>
                        <option value="">All</option>
                        {dataTypes.map(value => <option key={value} value={value}>{value}</option>)}
                    </select>
                </label>
            </div>

            <div style={{ 
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                border: '1px solid #ddd',
                paddingBottom: '12px',
                boxSizing: 'border-box',
            }}>
            <table style={{ 
                width: '100%', 
                borderCollapse: 'separate', // Needed for sticky header
                borderSpacing: 0,
                fontSize: '14px'
            }}>
                <thead>
                    <tr>
                        <th style={{ 
                            borderBottom: '1px solid #ddd', borderRight: '1px solid #ddd',
                            padding: '8px', backgroundColor: '#f2f2f2', textAlign: 'left',
                            position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap'
                        }}>
                            actions
                        </th>
                        {columns.map(col => (
                            <th key={col} style={{ 
                                borderBottom: '1px solid #ddd', 
                                borderRight: '1px solid #ddd',
                                padding: '8px', 
                                backgroundColor: '#f2f2f2', 
                                textAlign: 'left',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                whiteSpace: 'nowrap'
                            }}>
                                {col}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredData.map((row, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                            <td style={{ borderBottom: '1px solid #ddd', borderRight: '1px solid #ddd', padding: '4px 8px', whiteSpace: 'nowrap' }}>
                                {uploadRowIndex === index ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <select value={uploadColumn} onChange={e => setUploadColumn(e.target.value)}
                                            style={{ fontSize: '12px', padding: '2px 4px', borderRadius: '3px', border: '1px solid #ccc' }}>
                                            {UPLOAD_COLUMNS.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <input ref={fileInputRef} type="file" style={{ fontSize: '11px', width: '150px' }} />
                                        <button disabled={uploading} onClick={() => handleUpload(row)}
                                            title="Upload" style={{
                                                fontSize: '14px', padding: '2px 6px', border: 'none', borderRadius: '3px', cursor: 'pointer',
                                                backgroundColor: uploading ? '#ccc' : '#4CAF50', color: 'white', lineHeight: 1,
                                            }}>
                                            {uploading ? '\u23F3' : '\u2713'}
                                        </button>
                                        <button onClick={() => setUploadRowIndex(null)}
                                            title="Cancel" style={{
                                                fontSize: '14px', padding: '2px 6px', border: 'none', borderRadius: '3px', cursor: 'pointer',
                                                backgroundColor: '#e0e0e0', color: '#333', lineHeight: 1,
                                            }}>
                                            {'\u2715'}
                                        </button>
                                    </div>
                                ) : (
                                    <button onClick={() => { setUploadRowIndex(index); setUploadColumn(UPLOAD_COLUMNS[0]); }}
                                        style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '3px', border: '1px solid #ccc', cursor: 'pointer' }}>
                                        Upload
                                    </button>
                                )}
                            </td>
                            {columns.map(col => {
                                const value = row[col];
                                const isJson = isJsonColumn(col, value);
                                const isGcs = isGcsColumn(col);
                                
                                let content;
                                if ((col === 'schedule_id' || col === 'dup_schedule_id') && value) {
                                    content = (
                                        <a href={`https://d.bepro11.com/_a/schedule/schedule/${value}`} target="_blank" rel="noopener noreferrer">
                                            {String(value)}
                                        </a>
                                    );
                                } else if (col === 'video_id' && value) {
                                    content = (
                                        <a href={`https://d.bepro11.com/_a/video/video/${value}`} target="_blank" rel="noopener noreferrer">
                                            {String(value)}
                                        </a>
                                    );
                                } else if (isJson && value) {
                                    content = (
                                        <button onClick={() => handleViewJson(col, value)}>
                                            View JSON
                                        </button>
                                    );
                                } else if (isGcs && typeof value === 'string' && value) {
                                    content = (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <a href={convertGcsToHttp(value)} target="_blank" rel="noopener noreferrer" style={{ marginRight: '5px' }}>
                                                Open GCS
                                            </a>
                                            {col === 'raw_images' && (
                                                <button onClick={() => handleViewImages(value, row['annotation'], row['label'], row['camera_type'], row['data_type'])}>
                                                    View Gallery
                                                </button>
                                            )}
                                        </div>
                                    );
                                } else {
                                    content = value !== null && value !== undefined ? String(value) : '';
                                }
                                
                                return (
                                    <td key={col} style={{ 
                                        borderBottom: '1px solid #ddd',
                                        borderRight: '1px solid #ddd',
                                        padding: '4px 8px',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {content}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            
            <JSONModal 
                isOpen={modalOpen} 
                onClose={() => setModalOpen(false)} 
                data={modalData} 
                title={modalTitle} 
            />

            <ImageViewer
                isOpen={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                gcsPrefix={currentImagePrefix}
                annotationPath={currentAnnotationPath}
                labelPath={currentLabelPath}
                cameraType={currentCameraType}
                dataType={currentDataType}
            />
        </div>
    );
};

export default DatasetTable;